import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchSales, fetchCustomers } from '@/services/sales';
import { queryKeys } from '@/lib/queryKeys';
import { parseDatabaseError } from '@/lib/errors';
import { Sale } from '@/types/models';
import { Modal } from '@/components/common/Modal';
import { ErrorAlert } from '@/components/common/ErrorAlert';
import { Pagination } from '@/components/common/Pagination';
import { SearchInput } from '@/components/common/SearchInput';
import { NewSaleForm } from '@/features/sales/NewSaleForm';
import { SaleDetail } from '@/features/sales/SaleDetail';
import {
  ShoppingCart,
  Plus,
  ChevronDown,
  CalendarDays,
  User,
  Loader2,
} from 'lucide-react';
import { PAGE_SIZE } from '@/lib/constants';

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-PK', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function formatCurrency(val: number | null | undefined): string {
  if (val == null) return '—';
  return `Rs. ${val.toLocaleString('en-PK')}`;
}

function PaymentStatusBadge({ status }: { status: string }) {
  const styles: Record<string, string> = {
    paid: 'bg-green-100 text-green-800',
    partial: 'bg-amber-100 text-amber-800',
    pending: 'bg-red-100 text-red-800',
    refunded: 'bg-gray-100 text-gray-800',
  };
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide ${
        styles[status] ?? 'bg-brand-bg text-brand-muted'
      }`}
    >
      {status}
    </span>
  );
}

export const SalesPage: React.FC = () => {
  const [search, setSearch] = useState('');
  const [customerId, setCustomerId] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(0);

  const [newSaleModalOpen, setNewSaleModalOpen] = useState(false);
  const [detailSaleId, setDetailSaleId] = useState<string | null>(null);

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: queryKeys.sales.list({ search, customerId, status: statusFilter, page }),
    queryFn: () => fetchSales({ search, customerId, status: statusFilter, page }),
    placeholderData: (prev) => prev,
  });

  const { data: customers = [] } = useQuery({
    queryKey: ['customers', 'select', ''],
    queryFn: () => fetchCustomers(),
  });

  const sales = data?.data ?? [];
  const totalCount = data?.count ?? 0;

  const handleSearch = (val: string) => {
    setSearch(val);
    setPage(0);
  };

  const handleNewSaleSuccess = (saleId: string) => {
    setNewSaleModalOpen(false);
    void refetch();
    setDetailSaleId(saleId);
  };

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-lg font-bold text-brand-black flex items-center space-x-2">
            <ShoppingCart className="w-5 h-5 text-brand-green" />
            <span>Sales & POS Invoices</span>
          </h1>
          <p className="text-xs text-brand-muted mt-0.5">
            {isLoading ? 'Loading…' : `${totalCount.toLocaleString('en-PK')} sale${totalCount !== 1 ? 's' : ''} recorded`}
          </p>
        </div>
        <button
          onClick={() => setNewSaleModalOpen(true)}
          className="inline-flex items-center space-x-2 px-4 py-2 bg-brand-green text-white text-sm font-bold rounded-md hover:bg-brand-green-hover transition-colors shadow-sm"
          id="new-sale-btn"
        >
          <Plus className="w-4 h-4" />
          <span>New Sale / POS</span>
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-2">
        <div className="flex-1">
          <SearchInput
            value={search}
            onChange={handleSearch}
            placeholder="Search invoice # or notes…"
          />
        </div>

        <div className="relative">
          <select
            value={customerId}
            onChange={(e) => { setCustomerId(e.target.value); setPage(0); }}
            className="h-9 pl-3 pr-8 bg-white border border-brand-border rounded-md text-xs text-brand-black focus:outline-none focus:ring-2 focus:ring-brand-green appearance-none"
          >
            <option value="">All Customers</option>
            {customers.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
          <ChevronDown className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-brand-muted" />
        </div>

        <div className="relative">
          <select
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}
            className="h-9 pl-3 pr-8 bg-white border border-brand-border rounded-md text-xs text-brand-black focus:outline-none focus:ring-2 focus:ring-brand-green appearance-none"
          >
            <option value="">All Payment Statuses</option>
            <option value="paid">Paid</option>
            <option value="partial">Partial</option>
            <option value="pending">Unpaid</option>
            <option value="refunded">Refunded</option>
          </select>
          <ChevronDown className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-brand-muted" />
        </div>
      </div>

      {/* Error */}
      {isError && (
        <ErrorAlert message={parseDatabaseError(error)} onRetry={() => refetch()} />
      )}

      {/* Loading */}
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 text-brand-green animate-spin" />
        </div>
      )}

      {/* Sales Table */}
      {!isLoading && !isError && (
        <>
          {sales.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <ShoppingCart className="w-12 h-12 text-brand-muted/30 mb-3" />
              <p className="text-sm font-semibold text-brand-black">No sales found</p>
              <p className="text-xs text-brand-muted mt-1">
                {search || customerId || statusFilter
                  ? 'Try adjusting your search or filters.'
                  : 'Complete your first sale using the New Sale button above.'}
              </p>
            </div>
          ) : (
            <div className="bg-white border border-brand-border rounded-xl overflow-hidden shadow-xs">
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="bg-brand-bg border-b border-brand-border text-brand-muted font-semibold uppercase tracking-wide">
                    <tr>
                      <th className="text-left px-4 py-3">Date</th>
                      <th className="text-left px-4 py-3">Invoice #</th>
                      <th className="text-left px-4 py-3">Customer</th>
                      <th className="text-right px-4 py-3">Total</th>
                      <th className="text-right px-4 py-3">Paid</th>
                      <th className="text-right px-4 py-3">Balance</th>
                      <th className="text-left px-4 py-3">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-brand-border">
                    {sales.map((sale: Sale) => {
                      const balanceDue = Math.max(0, sale.balance_due ?? (sale.total - sale.amount_paid));
                      return (
                        <tr
                          key={sale.id}
                          onClick={() => setDetailSaleId(sale.id)}
                          className="hover:bg-brand-bg/60 cursor-pointer transition-colors"
                        >
                          <td className="px-4 py-3 text-brand-muted whitespace-nowrap">
                            <span className="flex items-center space-x-1.5">
                              <CalendarDays className="w-3.5 h-3.5 shrink-0 text-brand-green" />
                              <span>{formatDate(sale.sale_date)}</span>
                            </span>
                          </td>
                          <td className="px-4 py-3 font-bold text-brand-black">
                            #{sale.invoice_number}
                          </td>
                          <td className="px-4 py-3 text-brand-muted">
                            <span className="flex items-center space-x-1.5 font-medium text-brand-black">
                              <User className="w-3.5 h-3.5 shrink-0 text-brand-muted" />
                              <span>{sale.customer?.name ?? 'Walk-in Customer'}</span>
                            </span>
                          </td>
                          <td className="px-4 py-3 text-right font-bold text-brand-black">
                            {formatCurrency(sale.total)}
                          </td>
                          <td className="px-4 py-3 text-right text-brand-green font-semibold">
                            {formatCurrency(sale.amount_paid)}
                          </td>
                          <td className={`px-4 py-3 text-right font-semibold ${balanceDue > 0 ? 'text-brand-red' : 'text-brand-muted'}`}>
                            {balanceDue > 0 ? formatCurrency(balanceDue) : '—'}
                          </td>
                          <td className="px-4 py-3">
                            <PaymentStatusBadge status={sale.payment_status} />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Pagination */}
          {totalCount > PAGE_SIZE && (
            <Pagination page={page} totalCount={totalCount} onPageChange={setPage} />
          )}
        </>
      )}

      {/* New Sale Modal */}
      <Modal
        isOpen={newSaleModalOpen}
        onClose={() => setNewSaleModalOpen(false)}
        title="New Sale / POS Counter"
        maxWidth="pos"
      >
        <NewSaleForm
          onSuccess={handleNewSaleSuccess}
          onCancel={() => setNewSaleModalOpen(false)}
        />
      </Modal>

      {/* Sale Detail & Invoice Modal */}
      <Modal
        isOpen={!!detailSaleId}
        onClose={() => setDetailSaleId(null)}
        title="Invoice Details"
        maxWidth="4xl"
      >
        {detailSaleId && (
          <SaleDetail
            saleId={detailSaleId}
            onClose={() => setDetailSaleId(null)}
          />
        )}
      </Modal>
    </div>
  );
};
